/**
 * THE STATE BEHIND MATERIAL REALITY.
 *
 * Two contracts, read and written from one screen:
 *
 *   GET  /execution-intelligence/projects/{id}/material-authority
 *   POST /execution/materials/{materialStatusId}/observations
 *
 * THE PROJECT-SWITCH RACE IS THE LOAD-BEARING GUARD HERE. This board is
 * reached with `?project_id=` in the URL, so a person switches projects
 * without the component ever unmounting. A slow list from the previous
 * project landing after the new one would put one job's delivery dates under
 * another job's name — which on this surface means sending a crew to the
 * wrong site. The list load runs through the repository's `createSequencer`,
 * and the loaded rows are cached UNDER THE PROJECT ID they came from, so a
 * stale response cannot be rendered under a different project even if it
 * reaches setState.
 *
 * Recording is separate on purpose: an observation is always stored, so a
 * failure to REFRESH the board after one must never read as a failure to
 * record. The refresh is fired and its result is reported independently.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createSequencer } from '../../lib/sequencer';
import { classifyWorkflowError } from '../../lib/workflowErrors';
import { executionIntelligenceApi } from '../../services/intelligenceService';
import { readMaterialListContract } from './materialPresentation';

export const useMaterialReality = (projectId) => {
  /** {projectId, materials, unreadable, counts, truncated} — keyed, always. */
  const [view, setView] = useState(null);
  const [contractError, setContractError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(Boolean(projectId));

  const seq = useRef(createSequencer()).current;

  const load = useCallback(async () => {
    if (!projectId) {
      seq.invalidate();
      setView(null);
      setContractError(null);
      setLoadError(null);
      setLoading(false);
      return;
    }
    const ticket = seq.next();
    /* The previous project's rows leave the screen the instant the project
       changes, rather than lingering under the new project's name while its
       own load is in flight. */
    setView((prev) => (prev && prev.projectId === projectId ? prev : null));
    setLoading(true);
    try {
      const payload =
        await executionIntelligenceApi.materialAuthority(projectId);
      if (!seq.isCurrent(ticket)) return;
      const read = readMaterialListContract(payload);
      if (!read.readable) {
        setContractError(read.reason);
        setView({
          projectId,
          materials: [],
          unreadable: [],
          counts: {},
          truncated: false,
        });
      } else {
        setContractError(null);
        setView({
          projectId,
          materials: read.materials,
          unreadable: read.unreadable,
          counts: read.counts,
          truncated: read.truncated,
        });
      }
      setLoadError(null);
    } catch (error) {
      if (!seq.isCurrent(ticket)) return;
      setLoadError(classifyWorkflowError(error));
      setView(null);
      setContractError(null);
    } finally {
      if (seq.isCurrent(ticket)) setLoading(false);
    }
  }, [projectId, seq]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * The second guard, independent of the sequencer: rows are read back only
   * for the project they were loaded for. A stale response that somehow
   * reached state still cannot be rendered under another project's name.
   */
  const current = useMemo(
    () => (view && view.projectId === projectId ? view : null),
    [view, projectId],
  );

  return {
    materials: current?.materials || [],
    unreadable: current?.unreadable || [],
    counts: current?.counts || {},
    truncated: current?.truncated || false,
    contractError,
    loadError,
    loading,
    reload: load,
  };
};

export default useMaterialReality;
